/**
 * What a response does to the queue the trainer is looking at, decided away from React.
 *
 * Pure: no React, no DOM, no query client, no i18next. `lib/useAttentionResponse` owns the write and
 * the caches; this owns the two rules that have to agree with the SERVER's own idea of what a
 * response suppresses, and it is where they can be tested.
 *
 * ── An occurrence is an anchor AND a reason, never an anchor alone ───────────────────────────
 * `AttentionQueueItem.anchorKey` is not unique across the queue: `model/attentionSections` already keys a
 * row on `<reason>|<anchor>` because two reasons share a weekly anchor — a client who lapsed and
 * whose goal needs renewing in the same ISO week both hang off that week. The write names both
 * (R7), so an optimistic removal that matched on the anchor alone took a second, unanswered row off
 * the screen and the next read put it back.
 *
 * ── The two spellings meet through the entity's own table, never through a capital letter ────
 * The wire says `MissedWorkout` and this app says `missedWorkout`, and `entities/client` owns the
 * one table that relates them in both directions. This module used to re-derive the wire spelling
 * by upper-casing the first letter and compare strings, which would have matched a member the table
 * deliberately drops and would have gone on matching after the backend renamed one. Comparing
 * through `attentionReasonOf` means a wire value this build cannot name matches NOTHING, which is
 * the same answer the row itself gets — an item this build cannot draw is not one it can clear.
 */

import { attentionReasonOf, type AttentionReason } from '~/entities/client';
import type { TrainWithMeModulesAttentionDtosResponsesAttentionQueueResponse as AttentionQueueResponse } from '~/shared/api/generated/model';

/** Which occurrence a response was about. */
export interface RespondedOccurrence {
  readonly anchorKey: string;
  readonly reason: AttentionReason;
}

/**
 * One cached queue answer with the responded occurrence taken out of it.
 *
 * `undefined` in, `undefined` out: a cache entry that holds nothing is left alone rather than
 * seeded with an empty queue, which would make an unanswered read look like an answered one.
 */
export function withoutResponded(
  queue: AttentionQueueResponse | undefined,
  responded: RespondedOccurrence,
): AttentionQueueResponse | undefined {
  if (queue?.items == null) return queue;
  return {
    ...queue,
    items: queue.items.filter(
      (item) =>
        !(
          item.anchorKey === responded.anchorKey &&
          attentionReasonOf(item.reason) === responded.reason
        ),
    ),
  };
}

/**
 * **What a cached queue answer should look like once a BATCH has settled.**
 *
 * The pre-batch answer, minus exactly the occurrences the server accepted. Nothing else.
 *
 * ── Why a batch cannot roll back the way a single write does ─────────────────────────────────
 * A single write snapshots the cache in its own `onMutate` and puts that snapshot back if the
 * server refuses. N writes fired together snapshot IN SEQUENCE, so the second snapshot already
 * lacks the row the first removed and the fifth lacks four — and on a wholesale refusal the LAST
 * rollback to land wins, restoring a cache missing up to N-1 rows. Offline, the closing read cannot
 * repair it either, so the Pulpit goes on showing fewer rows than the trainer has.
 *
 * So a batch takes ONE snapshot before it removes anything, and settles by rebuilding from it. A
 * wholesale refusal means `accepted` is empty and every row comes back exactly where it was; a
 * partial one takes out precisely the rows the server took.
 */
export function afterBatch(
  before: AttentionQueueResponse | undefined,
  accepted: readonly RespondedOccurrence[],
): AttentionQueueResponse | undefined {
  let queue = before;
  for (const occurrence of accepted) queue = withoutResponded(queue, occurrence);
  return queue;
}
